import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import PurchaseNatureMissing from "../components/PurchaseNatureMissing";
import PurchasesToValidate from "../components/PurchasesToValidate";
import InvoicePdfButton from "../components/InvoicePdfButton";
import AccountPicker from "../components/AccountPicker";
import DocBadge from "../components/DocBadge";
import { purchaseAccountGroups } from "../utils/accountGroups";
import { openBlobInNewTab } from "../utils/openBlob";

const DISC_EMPTY_F = { invoice_ref: "", client_name: "", rate: "", amount: "", discount_days: "10", payment_date: "", notes: "" };

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(String(d).replace(" ", "T")).toLocaleDateString("fr-FR"); }
// « 2026-09 » → « Septembre 2026 » (onglets des factures fournisseurs)
function monthLabel(m) { if (!/^\d{4}-\d{2}$/.test(m || "")) return m || "—"; const s = new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); }

const TABS = [
  { id: "suppliers", label: "Fournisseurs",         icon: "🏭" },
  { id: "orders",    label: "Commandes",             icon: "🛒" },
  { id: "discounts", label: "Escomptes fournisseurs", icon: "💸" },
  { id: "ocr",       label: "Factures OCR",           icon: "📄" },
  { id: "tovalidate", label: "À valider",             icon: "✅" },
  { id: "purchases", label: "Factures fournisseurs",  icon: "🧾" },
];
// « Déjà réglée — à rapprocher » (J1, 02/11/2026) : marquée réglée, paiement pas encore relié au relevé
const SETTLED_MARKED = "réglée, à rapprocher";
const isToPay = (p) => p.payment_state === "à payer" || p.payment_state === "partiellement payée";

const ORDER_STATUS = {
  draft:     { label: "Brouillon",  color: "#64748b", bg: "#64748b22" },
  sent:      { label: "Envoyée",    color: "#ba7517", bg: "#ba751722" },
  partial:   { label: "Partielle",  color: "#185fa5", bg: "#185fa522" },
  received:  { label: "Reçue",      color: "#1a9e72", bg: "#1a9e7222" },
  cancelled: { label: "Annulée",    color: "#e24b4a", bg: "#e24b4a22" },
};

const EMPTY_SUPPLIER = { name: "", code: "", email: "", phone: "", address: "", city: "", siret: "", tva_number: "", payment_delay: "30", notes: "" };

export default function Achats({ currentCompanyId, onNavigate }) {
  const [tab, setTab]             = useState(() => {
    try { if (sessionStorage.getItem("execpro360.intent") === "purchasesImport") { sessionStorage.removeItem("execpro360.intent"); return "ocr"; } } catch {}
    return "suppliers";
  });
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(EMPTY_SUPPLIER);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [search, setSearch]       = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Escomptes fournisseurs
  const [discounts, setDiscounts]     = useState([]);
  const [showDiscModal, setShowDisc]  = useState(false);
  const [editDisc, setEditDisc]       = useState(null);
  const [discForm, setDiscForm]       = useState(DISC_EMPTY_F);
  const [savingDisc, setSavingDisc]   = useState(false);
  const [discError, setDiscError]     = useState("");

  // Factures fournisseurs OCR (staging)
  const [ocrInvoices, setOcrInvoices]   = useState([]);
  const [ocrLoading, setOcrLoading]     = useState(false);
  const [ocrUploading, setOcrUploading] = useState(false);
  const [ocrError, setOcrError]         = useState("");
  // Refus de validation affiché sous la facture concernée (04/11/2026) : le message du haut
  // de liste restait hors de vue quand on validait une facture plus bas
  const [ocrRowError, setOcrRowError]   = useState(null);
  const [ocrEditId, setOcrEditId]       = useState(null);
  const [ocrEditForm, setOcrEditForm]   = useState({});
  const [ocrActionId, setOcrActionId]   = useState(null);
  // Import en lot : un résultat par fichier { name, status, message }
  const [ocrBatch, setOcrBatch]         = useState([]);
  const [ocrBatchId, setOcrBatchId]     = useState(null);
  // Rejet : sélection multiple, fenêtre de confirmation, trace des rejets
  const [ocrSelected, setOcrSelected]   = useState([]);
  const [ocrBatchValidating, setOcrBatchValidating] = useState(false);
  const [ocrBatchResult, setOcrBatchResult] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null); // { kind: 'one'|'many'|'batch', ids?, inv?, batchId? }
  const [rejectReason, setRejectReason] = useState("");
  const [rejectDeletePdf, setRejectDeletePdf] = useState(false);
  const [rejecting, setRejecting]       = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  // Doublon probable : { inv, existing, entry, message }
  const [dupTarget, setDupTarget]       = useState(null);
  const [dupReason, setDupReason]       = useState("");
  const [supplierCodes, setSupplierCodes] = useState({}); // reprise d'activité : code expert facultatif, par facture
  const [rejectedList, setRejectedList] = useState([]);

  // Comptes du plan de la société, proposés sur le compte de « Corriger » (champ libre)
  const [planAccounts, setPlanAccounts] = useState([]);
  useEffect(() => {
    if (currentCompanyId) api.chartAccounts.list(currentCompanyId).then(l => setPlanAccounts(Array.isArray(l) ? l : [])).catch(() => setPlanAccounts([]));
  }, [currentCompanyId]);

  // Factures fournisseurs validées (purchase_invoices)
  const [purchases, setPurchases]         = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  // Vue (lot 2, 28/10/2026) : « À payer » d'abord, puis un onglet par mois non soldé ; mois soldés dans un menu
  const [purchasesView, setPurchasesView] = useState('to_pay'); // 'to_pay' | 'settled_marked' | 'all' | 'AAAA-MM'
  const [purchaseMonths, setPurchaseMonths] = useState({ months: [], to_pay: { count: 0, remaining: 0 }, settled_marked: { count: 0, remaining: 0 } });
  const [purchasesRemaining, setPurchasesRemaining] = useState(0);
  const [purchaseTotals, setPurchaseTotals] = useState({ to_pay_by_supplier: [] });
  // J1 (02/11/2026) : filtre fournisseur, tri par date, sélection pour le marqueur « Déjà réglée »
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateSort, setDateSort] = useState("desc");
  const [markSelected, setMarkSelected] = useState([]);
  const [markBusy, setMarkBusy] = useState(false);
  const [markResult, setMarkResult] = useState(null);
  const [onlyToPay, setOnlyToPay] = useState(false); // onglets mensuels : factures non soldées seulement (02/11/2026)
  const [cancelTarget, setCancelTarget]   = useState(null);
  const [cancelReason, setCancelReason]   = useState('');
  const [cancelling, setCancelling]       = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null); // { loading } | { error } | contre-passation prévue
  // Modifier la nature d'un achat validé (administrateur, comptable)
  const { user } = useAuth();
  const canChangeNature = ["admin", "comptable"].includes(user?.role);
  const [natureTarget, setNatureTarget] = useState(null);  // { p, nature, onDebits }
  const [naturePreview, setNaturePreview] = useState(null); // { loading } | { error } | aperçu
  const [purchasesError, setPurchasesError] = useState('');

  const loadSuppliers = useCallback(() => {
    if (!currentCompanyId) return;
    api.suppliers.list(currentCompanyId, search || undefined)
      .then(data => setSuppliers(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [currentCompanyId, search]);

  const loadOrders = useCallback(() => {
    if (!currentCompanyId) return;
    api.stocks.orders(currentCompanyId, {})
      .then(r => setOrders(Array.isArray(r) ? r : []))
      .catch(console.error);
  }, [currentCompanyId]);

  const loadAll = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.suppliers.list(currentCompanyId),
      api.stocks.orders(currentCompanyId, {}),
    ]).then(([s, o]) => {
      setSuppliers(Array.isArray(s) ? s : []);
      setOrders(Array.isArray(o) ? o : []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  const loadDiscounts = useCallback(() => {
    if (!currentCompanyId) return;
    api.accounting.discounts(currentCompanyId, 'fournisseur').then(setDiscounts).catch(console.error);
  }, [currentCompanyId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (search !== undefined) loadSuppliers(); }, [loadSuppliers, search]);
  useEffect(() => { if (tab === 'discounts') loadDiscounts(); }, [tab, loadDiscounts]);

  // Validation par lot : factures sans anomalie (le serveur re-contrôle, et
  // écarte tout doublon possible)
  const ocrEligible = ocrInvoices.filter(i => Array.isArray(i.anomalies) && i.anomalies.length === 0);
  const validateOcrBatch = async () => {
    if (!window.confirm(`Valider et comptabiliser ${ocrEligible.length} facture(s) d'achat sans anomalie ?`)) return;
    setOcrBatchValidating(true); setOcrError(""); setOcrBatchResult(null);
    try {
      const r = await api.ocrInvoices.validateBatch(currentCompanyId, ocrEligible.map(i => i.id));
      setOcrBatchResult(r); setOcrSelected([]);
      loadOcrInvoices();
    } catch (err) { setOcrError(err?.message || "Erreur lors de la validation par lot."); }
    finally { setOcrBatchValidating(false); }
  };

  const [priorInvoices, setPriorInvoices] = useState([]);
  const loadOcrInvoices = useCallback(() => {
    if (!currentCompanyId) return;
    setOcrLoading(true);
    api.ocrInvoices.list(currentCompanyId, { status: 'staging', limit: 500 })
      .then(data => setOcrInvoices(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setOcrLoading(false));
    // Exercice antérieur : rubrique séparée (reprises par le FEC de l'expert-comptable)
    api.ocrInvoices.priorExercise(currentCompanyId).then(d => setPriorInvoices(d?.invoices || [])).catch(() => setPriorInvoices([]));
  }, [currentCompanyId]);
  useEffect(() => { if (tab === 'ocr') loadOcrInvoices(); }, [tab, loadOcrInvoices]);

  const loadPurchases = useCallback(() => {
    if (!currentCompanyId) return;
    setPurchasesLoading(true);
    api.purchases.months().then(setPurchaseMonths).catch(() => {});
    const params = ['to_pay', 'settled_marked', 'all'].includes(purchasesView) ? { [purchasesView === 'all' ? 'all' : purchasesView]: 1 } : { month: purchasesView };
    api.purchases.list(params)
      .then(r => {
        const list = Array.isArray(r?.purchases) ? r.purchases : (Array.isArray(r) ? r : []);
        setPurchases(list);
        setPurchasesRemaining(Number(r?.remaining) || 0);
        setPurchaseTotals({ to_pay_by_supplier: r?.to_pay_by_supplier || [] });
        setMarkSelected([]);
      })
      .catch(err => setPurchasesError(err.message))
      .finally(() => setPurchasesLoading(false));
  }, [currentCompanyId, purchasesView]);
  useEffect(() => { if (tab === 'purchases') loadPurchases(); }, [tab, loadPurchases]);

  // Marqueur « Déjà réglée — à rapprocher » : aucune écriture ; réversible ; tracé
  async function markSettled(ids, marked) {
    if (!ids.length) return;
    if (marked && !window.confirm(`Marquer ${ids.length} facture(s) « Déjà réglée — à rapprocher » ?\n\nAucune écriture n'est passée : elles sortent du « À payer » mais restent dues en comptabilité tant que leur paiement n'est pas relié au relevé. Le marqueur se lève de lui-même au rapprochement.`)) return;
    setMarkBusy(true); setMarkResult(null); setPurchasesError("");
    try { setMarkResult(await api.purchases.settledMark(ids, marked)); loadPurchases(); }
    catch (err) { setPurchasesError(err.message); }
    finally { setMarkBusy(false); }
  }
  useEffect(() => { setSupplierFilter(""); setMarkResult(null); }, [purchasesView]);
  // Lignes affichées : vue, « À payer seulement », fournisseur, tri par date
  const visiblePurchases = purchases
    .filter(p => ["to_pay", "settled_marked"].includes(purchasesView) || !onlyToPay || isToPay(p))
    .filter(p => !supplierFilter || (p.supplier_name || "—") === supplierFilter)
    .sort((a, b) => (dateSort === "asc" ? 1 : -1) * (String(a.date || "").localeCompare(String(b.date || "")) || a.id - b.id));

  // Ouverture de la fenêtre d'annulation : contre-passation prévue (ou motif de refus) avant toute action
  // Nature : aperçu recalculé à chaque choix, exécution après confirmation
  async function previewNature(p, nature, onDebits) {
    setNatureTarget({ p, nature, onDebits }); setNaturePreview({ loading: true });
    try { setNaturePreview(await api.purchases.natureChangePreview(p.id, nature, onDebits)); }
    catch (err) { setNaturePreview({ error: err.message }); }
  }
  const openNature = (p) => previewNature(p, p.operation_nature === "biens" ? "services" : "biens", false);
  const closeNature = () => { setNatureTarget(null); setNaturePreview(null); };
  async function confirmNature() {
    if (!natureTarget || !naturePreview || naturePreview.loading || naturePreview.error) return;
    setCancelling(true); setPurchasesError('');
    try {
      await api.purchases.natureChange(natureTarget.p.id, { operation_nature: natureTarget.nature, supplier_vat_on_debits: natureTarget.onDebits });
      closeNature(); loadPurchases();
    } catch (err) { setNaturePreview({ error: err.message }); }
    finally { setCancelling(false); }
  }

  async function openCancel(p) {
    setCancelTarget(p); setCancelPreview({ loading: true });
    try { setCancelPreview(await api.purchases.cancelPreview(p.id)); }
    catch (err) { setCancelPreview({ error: err.message }); }
  }
  const closeCancel = () => { setCancelTarget(null); setCancelReason(''); setCancelPreview(null); };

  async function confirmCancel() {
    if (!cancelTarget || !cancelPreview || cancelPreview.loading || cancelPreview.error) return;
    setCancelling(true); setPurchasesError('');
    try {
      await api.purchases.cancel(cancelTarget.id, cancelReason || undefined);
      closeCancel();
      loadPurchases();
    } catch (err) { setPurchasesError(err.message); }
    finally { setCancelling(false); }
  }

  const PURCHASE_STATUS = {
    draft:     { label: "Brouillon", color: "#64748b", bg: "#64748b22" },
    validated: { label: "Validée",   color: "#1a9e72", bg: "#1a9e7222" },
    paid:      { label: "Payée",     color: "#185fa5", bg: "#185fa522" },
    cancelled: { label: "Annulée",   color: "#e24b4a", bg: "#e24b4a22" },
  };

  // Import d'un ou plusieurs PDF : envoi FICHIER PAR FICHIER (une requête
  // chacun, jamais un lot en une seule requête — limite de taille et délai
  // de lecture IA par requête), progression et résultat par fichier. Un
  // échec n'interrompt pas les suivants.
  async function handleOcrFileSelect(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset pour permettre de re-sélectionner les mêmes fichiers
    if (!files.length || !currentCompanyId) return;
    const batch = files.map(f => (f.type === 'application/pdf'
      ? { name: f.name, status: 'pending', message: '' }
      : { name: f.name, status: 'error', message: 'Seuls les fichiers PDF sont acceptés.' }));
    // Identifiant du lot : permet d'annuler d'un coup tout ce lot d'import
    const batchId = (window.crypto?.randomUUID?.() || `lot-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    setOcrBatchId(batchId);
    setOcrError(""); setOcrUploading(true); setOcrBatch([...batch]);
    for (let i = 0; i < files.length; i++) {
      if (batch[i].status === 'error') continue;
      batch[i] = { ...batch[i], status: 'running' }; setOcrBatch([...batch]);
      try {
        const r = await api.ocrInvoices.upload(currentCompanyId, files[i], batchId);
        // Importée mais à compléter (illisible, fournisseur = votre société…)
        batch[i] = r?.warning?.message
          ? { ...batch[i], status: 'warning', message: r.warning.message }
          : { ...batch[i], status: 'done', message: [r?.supplier_name, r?.amount_ttc != null ? `${fmt(r.amount_ttc)} € TTC` : null].filter(Boolean).join(' · ') };
      } catch (err) {
        batch[i] = { ...batch[i], status: 'error', message: err.message };
      }
      setOcrBatch([...batch]);
      loadOcrInvoices();
    }
    setOcrUploading(false);
  }

  function openOcrEdit(inv) {
    setOcrEditId(inv.id);
    setOcrEditForm({
      supplier_name: inv.supplier_name || '',
      invoice_ref: inv.invoice_ref || '',
      invoice_date: inv.invoice_date || '',
      amount_ht: inv.amount_ht != null ? String(inv.amount_ht) : '',
      amount_tva: inv.amount_tva != null ? String(inv.amount_tva) : '',
      amount_ttc: inv.amount_ttc != null ? String(inv.amount_ttc) : '',
      suggested_account: inv.suggested_account || '',
      vat_regime: inv.vat_regime || '',
      autoliq_rate: inv.autoliq_rate != null ? String(inv.autoliq_rate) : '20',
      vat_exemption_article: inv.vat_exemption_article || '',
      operation_nature: inv.operation_nature || '',
      supplier_vat_on_debits: !!inv.supplier_vat_on_debits,
      deductibility_rule: inv.deductibility_rule || 'standard',
      vehicle_type: inv.vehicle_type || 'tourisme',
    });
  }

  async function saveOcrEdit(id) {
    try {
      await api.ocrInvoices.update(id, ocrEditForm);
      setOcrEditId(null);
      loadOcrInvoices();
    } catch (err) { setOcrError(err.message); }
  }

  // Validation. Doublon probable (409) : fenêtre « C'est une facture
  // distincte » au lieu d'un message technique ; la confirmation est tracée
  // au journal d'audit côté serveur.
  async function validateOcrInvoice(inv, extra = {}) {
    setOcrActionId(inv.id); setOcrError(""); setOcrRowError(null);
    try {
      const editingThis = ocrEditId === inv.id;
      const account = (editingThis ? ocrEditForm.suggested_account : inv.suggested_account) || undefined;
      // Nature (biens / services) et option débits : confirmées en validant
      const nature = (editingThis ? ocrEditForm.operation_nature : inv.operation_nature) || undefined;
      const onDebits = editingThis ? !!ocrEditForm.supplier_vat_on_debits : (inv.supplier_vat_on_debits != null ? !!inv.supplier_vat_on_debits : undefined);
      const r = await api.ocrInvoices.validate(inv.id, {
        ...(account ? { compte_comptable: account } : {}),
        ...(nature ? { operation_nature: nature } : {}),
        ...(onDebits !== undefined ? { supplier_vat_on_debits: onDebits } : {}),
        // Droit à déduction de la TVA : confirmé en validant
        deductibility_rule: (editingThis ? ocrEditForm.deductibility_rule : inv.deductibility_rule) || 'standard',
        vehicle_type: (editingThis ? ocrEditForm.vehicle_type : inv.vehicle_type) || 'tourisme',
        // Reprise d'activité : nouveau fournisseur créé sous le code de l'expert-comptable s'il est saisi
        ...(inv.new_supplier && supplierCodes[inv.id] ? { supplier_account_code: supplierCodes[inv.id] } : {}),
        ...extra,
      });
      setOcrEditId(null); setDupTarget(null);
      if (r?.warning?.message) setOcrError(`Validée (${r.number}). ${r.warning.message}`);
      loadOcrInvoices();
    } catch (err) {
      if (err.code === 'POSSIBLE_DUPLICATE') { setDupTarget({ inv, existing: err.data?.existing_invoice || null, entry: err.data?.existing_entry || null, message: err.message }); setDupReason(""); }
      else { setOcrError(err.message); setOcrRowError({ id: inv.id, message: err.message }); }
    }
    finally { setOcrActionId(null); }
  }

  // Rejet : une facture, la sélection, ou tout un lot d'import. Ouvre une
  // fenêtre de confirmation (motif + suppression facultative des PDF).
  function openReject(target) {
    setRejectTarget(target); setRejectReason(""); setRejectDeletePdf(false);
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    setRejecting(true); setOcrError("");
    const reason = rejectReason.trim() || 'Rejeté manuellement';
    try {
      let r;
      if (rejectTarget.kind === 'one') r = await api.ocrInvoices.reject(rejectTarget.inv.id, reason, rejectDeletePdf);
      else if (rejectTarget.kind === 'many') r = await api.ocrInvoices.rejectBulk(rejectTarget.ids, reason, rejectDeletePdf);
      else r = await api.ocrInvoices.rejectBatch(rejectTarget.batchId, reason, rejectDeletePdf);
      const kept = r?.already_validated?.length
        ? ` ${r.already_validated.length} facture(s) du lot déjà validée(s) n'ont pas été modifiées (à annuler depuis « Factures validées »).` : '';
      setOcrError(`${r?.rejected ?? 0} facture(s) rejetée(s)${rejectDeletePdf ? ', PDF supprimé(s)' : ', PDF conservé(s)'}.${kept}`);
      setRejectTarget(null); setOcrSelected([]);
      loadOcrInvoices();
      if (showRejected) loadRejected();
    } catch (err) { setOcrError(err.message); }
    finally { setRejecting(false); }
  }

  const loadRejected = useCallback(() => {
    if (!currentCompanyId) return;
    api.ocrInvoices.list(currentCompanyId, { status: 'rejected', limit: 200 })
      .then(data => setRejectedList(Array.isArray(data) ? data : []))
      .catch(err => setOcrError(err.message));
  }, [currentCompanyId]);
  useEffect(() => { if (showRejected) loadRejected(); }, [showRejected, loadRejected]);

  const VAT_REGIME_LABEL = { franchise: 'Franchise (293 B)', autoliquidation: 'Autoliquidation', exoneration: 'Exonération' };

  // Pipeline à 2 niveaux (natif -> IA) — l'ancien palier Tesseract
  // intermédiaire (2) a été retiré, jamais fonctionnel en production. Le
  // modèle exact du niveau 3 (Mistral Small 4 ou repli Mistral OCR) n'est
  // pas conservé par facture, donc libellé
  // générique plutôt que de mentir sur un provider figé.
  const OCR_LEVEL_LABEL = { 1: 'PDF natif', 3: 'IA' };
  function confidenceBadge(score) {
    if (score == null) return { label: '—', color: '#64748b', bg: '#64748b22' };
    if (score >= 0.8) return { label: `${Math.round(score * 100)}%`, color: '#1a9e72', bg: '#1a9e7222' };
    if (score >= 0.55) return { label: `${Math.round(score * 100)}%`, color: '#ba7517', bg: '#ba751722' };
    return { label: `${Math.round(score * 100)}%`, color: '#e24b4a', bg: '#e24b4a22' };
  }

  function openCreate() { setEditTarget(null); setForm(EMPTY_SUPPLIER); setError(""); setShowModal(true); }
  function openEdit(s) {
    setEditTarget(s);
    setForm({ name: s.name || "", code: s.code || "", email: s.email || "", phone: s.phone || "", address: s.address || "", city: s.city || "", siret: s.siret || "", tva_number: s.tva_number || "", payment_delay: String(s.payment_delay || 30), notes: s.notes || "" });
    setError(""); setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, company_id: currentCompanyId };
      if (editTarget) await api.suppliers.update(editTarget.id, payload);
      else await api.suppliers.create(payload);
      setShowModal(false); loadSuppliers();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    await api.suppliers.delete(id).catch(console.error);
    setConfirmDelete(null); loadSuppliers();
  }

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function openCreateDisc() { setEditDisc(null); setDiscForm(DISC_EMPTY_F); setDiscError(""); setShowDisc(true); }
  function openEditDisc(d) {
    setEditDisc(d);
    setDiscForm({ invoice_ref: d.invoice_ref || "", client_name: d.client_name || "", rate: String(d.rate), amount: String(d.amount), discount_days: String(d.discount_days || 10), payment_date: d.payment_date || "", notes: d.notes || "" });
    setDiscError(""); setShowDisc(true);
  }
  async function handleSaveDisc(e) {
    e.preventDefault(); setDiscError(""); setSavingDisc(true);
    try {
      const payload = { ...discForm, company_id: currentCompanyId, type: "fournisseur", rate: parseFloat(discForm.rate), amount: parseFloat(discForm.amount || 0), discount_days: parseInt(discForm.discount_days) };
      if (editDisc) await api.accounting.updateDiscount(editDisc.id, payload);
      else await api.accounting.createDiscount(payload);
      setShowDisc(false); loadDiscounts();
    } catch (err) { setDiscError(err.message); }
    finally { setSavingDisc(false); }
  }
  async function handleDeleteDisc(id) {
    if (!window.confirm("Supprimer cet escompte ?")) return;
    await api.accounting.deleteDiscount(id).catch(console.error); loadDiscounts();
  }

  const kpiTotal    = orders.length;
  const kpiPending  = orders.filter(o => o.status === "sent" || o.status === "partial").length;
  const kpiReceived = orders.filter(o => o.status === "received").length;
  const kpiAmount   = orders.reduce((s, o) => s + (o.total_ht || 0), 0);

  if (!currentCompanyId) return (
    <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🛒</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Sélectionnez une société</div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h2 className="page-title">Gestion des achats</h2>
        {tab === "suppliers" && (
          <button className="btn primary" onClick={openCreate}>+ Nouveau fournisseur</button>
        )}
      </div>

      {/* KPIs */}
      <div className="kpi-grid k4" style={{ margin: "16px 0" }}>
        <div className="kpi">
          <div className="kpi-label">🏭 Fournisseurs actifs</div>
          <div className="kpi-value">{suppliers.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">🛒 Commandes totales</div>
          <div className="kpi-value">{kpiTotal}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">⏳ En cours</div>
          <div className="kpi-value" style={{ color: "var(--amber)" }}>{kpiPending}</div>
          <div className="kpi-sub">En attente de réception</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">💶 Montant HT commandes</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(kpiAmount)} €</div>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--border-color, #e2e8f0)", marginBottom: 20, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "9px 16px", fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
            cursor: "pointer", border: "none", background: "transparent",
            color: tab === t.id ? "var(--g)" : "var(--text-muted)",
            borderBottom: `2px solid ${tab === t.id ? "var(--g)" : "transparent"}`,
            marginBottom: -2, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>{t.icon}</span><span>{t.label}</span>
            {t.id === "orders" && kpiPending > 0 && (
              <span style={{ fontSize: 10, background: "#ba7517", color: "#fff", padding: "1px 5px", borderRadius: 8, fontWeight: 700 }}>{kpiPending}</span>
            )}
          </button>
        ))}
      </div>

      {/* Onglet Fournisseurs */}
      {tab === "suppliers" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input className="input" placeholder="Rechercher un fournisseur…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
          ) : suppliers.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏭</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun fournisseur</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>Ajoutez vos fournisseurs pour gérer vos achats et commandes.</div>
              <button className="btn primary" onClick={openCreate}>+ Créer un fournisseur</button>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
              {suppliers.map(s => (
                <div key={s.id} className="card">
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: "#1a9e7218", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🏭</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{s.name}</div>
                      {s.code && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Code : {s.code}</div>}
                      {s.email && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>✉ {s.email}</div>}
                      {s.phone && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>📞 {s.phone}</div>}
                      {s.city  && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>📍 {s.city}</div>}
                      {s.payment_delay && <div style={{ fontSize: 11, color: "#185fa5", marginTop: 4 }}>Délai paiement : {s.payment_delay}j</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => openEdit(s)}>✏️</button>
                      <button className="btn" style={{ fontSize: 11, padding: "3px 8px", color: "#e24b4a" }} onClick={() => setConfirmDelete(s)}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet Commandes */}
      {tab === "orders" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
            <button className="btn primary" onClick={() => window.location.hash = "#/stocks"}>
              🛒 Gérer les commandes dans Stocks
            </button>
          </div>
          {orders.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucune commande</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Les commandes fournisseurs sont gérées dans le module Stocks.</div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Référence</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Fournisseur</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Date commande</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Date attendue</th>
                    <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Total HT</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const sc = ORDER_STATUS[o.status] || ORDER_STATUS.draft;
                    const sup = suppliers.find(s => s.id === o.supplier_id);
                    return (
                      <tr key={o.id} style={{ borderTop: "var(--border)", fontSize: 13 }}>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 600 }}>{o.order_ref}</td>
                        <td style={{ padding: "10px 14px" }}>{sup?.name || o.supplier_name || "—"}</td>
                        <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{fmtDate(o.order_date)}</td>
                        <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{fmtDate(o.expected_date)}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{fmt(o.total_ht)} €</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Onglet Escomptes fournisseurs */}
      {tab === "discounts" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Escomptes fournisseurs obtenus</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Remises accordées par vos fournisseurs pour paiement anticipé — compte 765</div>
            </div>
            <button className="btn primary" onClick={openCreateDisc}>+ Escompte fournisseur</button>
          </div>
          {discounts.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💸</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun escompte enregistré</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>Enregistrez les remises obtenues de vos fournisseurs pour paiement anticipé.</div>
              <button className="btn primary" onClick={openCreateDisc}>+ Créer un escompte</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Réf. facture</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Fournisseur</th>
                    <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Taux</th>
                    <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Montant</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Délai</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Paiement</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Écriture</th>
                    <th style={{ padding: "9px 14px", fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map(d => (
                    <tr key={d.id} style={{ borderTop: "var(--border)", fontSize: 13 }}>
                      <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 600, color: "var(--blue)" }}>{d.invoice_ref || '—'}</td>
                      <td style={{ padding: "10px 14px" }}>{d.client_name || '—'}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>{d.rate}%</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#1a9e72" }}>{fmt(d.amount)} €</td>
                      <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{d.discount_days || 0}j</td>
                      <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{d.payment_date ? fmtDate(d.payment_date) : '—'}</td>
                      <td style={{ padding: "10px 14px", fontSize: 11, color: "#185fa5", fontFamily: "monospace" }}>D 401 / C 765</td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => openEditDisc(d)}>✏️</button>
                          <button className="btn" style={{ fontSize: 11, padding: "3px 8px", color: "#e24b4a" }} onClick={() => handleDeleteDisc(d.id)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#1a9e7208", border: "1px solid #1a9e7220", borderRadius: 8, fontSize: 12, color: "var(--text-muted)" }}>
            <strong>PCG :</strong> Escompte obtenu → D&nbsp;401 (diminution dette fournisseur) / C&nbsp;765 (produits financiers) — Impact positif sur le résultat financier.
          </div>
        </div>
      )}

      {/* Onglet Factures OCR */}
      {tab === "ocr" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Factures fournisseurs — extraction automatique</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Upload PDF → extraction OCR → vérification → comptabilisation</div>
            </div>
            <label className="btn primary" style={{ cursor: ocrUploading ? "wait" : "pointer" }}>
              {ocrUploading
                ? `Analyse en cours… ${ocrBatch.filter(b => b.status !== 'pending' && b.status !== 'running').length}/${ocrBatch.length}`
                : "+ Importer des factures PDF"}
              <input type="file" accept="application/pdf" multiple onChange={handleOcrFileSelect} disabled={ocrUploading} style={{ display: "none" }} />
            </label>
          </div>

          {ocrBatch.length > 0 && (
            <div className="card" style={{ marginBottom: 12, padding: "10px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  Import : {ocrBatch.filter(b => b.status === 'done').length} lue(s)
                  {ocrBatch.some(b => b.status === 'warning') && `, ${ocrBatch.filter(b => b.status === 'warning').length} à compléter`}
                  {ocrBatch.some(b => b.status === 'error') && `, ${ocrBatch.filter(b => b.status === 'error').length} en échec`}
                  {" "}sur {ocrBatch.length}
                </div>
                {!ocrUploading && (
                  <div style={{ display: "flex", gap: 6 }}>
                    {ocrBatchId && ocrBatch.some(b => b.status === 'done' || b.status === 'warning') && (
                      <button className="btn" style={{ fontSize: 11, padding: "3px 8px", color: "#e24b4a" }}
                        onClick={() => openReject({ kind: 'batch', batchId: ocrBatchId })}>Annuler ce lot</button>
                    )}
                    <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => setOcrBatch([])}>Masquer</button>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
                {ocrBatch.map((b, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, padding: "4px 8px", background: "#f9fafb", borderRadius: 6 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</span>
                    <span style={{ flexShrink: 0, maxWidth: "60%", textAlign: "right", color: { done: "#1a9e72", warning: "#ba7517", error: "#e24b4a" }[b.status] || "var(--text-muted)" }}>
                      {b.status === 'pending' && "en attente"}
                      {b.status === 'running' && "lecture…"}
                      {b.status === 'done' && `✓ ${b.message || 'importée'}`}
                      {b.status === 'warning' && `⚠ ${b.message}`}
                      {b.status === 'error' && `✗ ${b.message}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ocrError && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{ocrError}</div>}

          {ocrInvoices.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10, padding: "10px 14px", background: "#f0fdf9", borderRadius: 10, fontSize: 13 }}>
              <span><strong>{ocrEligible.length}</strong> facture(s) sans anomalie sur {ocrInvoices.length}{ocrInvoices.length - ocrEligible.length > 0 ? ` — ${ocrInvoices.length - ocrEligible.length} à vérifier une par une (motif indiqué)` : ""}.</span>
              <button className="btn primary" disabled={!ocrEligible.length || ocrBatchValidating} onClick={validateOcrBatch}>
                {ocrBatchValidating ? "Validation en cours…" : `✓ Valider les ${ocrEligible.length} factures sans anomalie`}
              </button>
            </div>
          )}
          {ocrBatchResult && (
            <div style={{ fontSize: 12.5, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, marginBottom: 10 }}>
              {ocrBatchResult.validated.length} facture(s) validée(s) et comptabilisée(s){ocrBatchResult.skipped.length ? `, ${ocrBatchResult.skipped.length} écartée(s) : ${ocrBatchResult.skipped.map(x => `${x.supplier_name || "?"} (${x.reasons.join(", ")})`).join(" ; ")}` : ""}{ocrBatchResult.errors.length ? `, ${ocrBatchResult.errors.length} en erreur : ${ocrBatchResult.errors.map(x => `${x.supplier_name || "?"} (${x.error})`).join(" ; ")}` : ""}.
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, fontSize: 12, flexWrap: "wrap" }}>
            {ocrInvoices.length > 0 && (
              <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox"
                  checked={ocrSelected.length > 0 && ocrSelected.length === ocrInvoices.length}
                  onChange={e => setOcrSelected(e.target.checked ? ocrInvoices.map(i => i.id) : [])} />
                Tout sélectionner
              </label>
            )}
            {ocrSelected.length > 0 && (
              <button className="btn" style={{ fontSize: 11, padding: "4px 10px", color: "#e24b4a" }}
                onClick={() => openReject({ kind: 'many', ids: ocrSelected })}>
                ✕ Rejeter la sélection ({ocrSelected.length})
              </button>
            )}
            <button className="btn" style={{ fontSize: 11, padding: "4px 10px", marginLeft: "auto" }} onClick={() => setShowRejected(v => !v)}>
              {showRejected ? "Masquer les rejets" : "Voir les factures rejetées"}
            </button>
          </div>

          {showRejected && (
            <div className="card" style={{ marginBottom: 12, padding: "10px 14px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Factures rejetées (trace conservée)</div>
              {rejectedList.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Aucune facture rejetée.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
                  {rejectedList.map(r => (
                    <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, padding: "4px 8px", background: "#f9fafb", borderRadius: 6 }}>
                      <span>{r.supplier_name || "Fournisseur non identifié"}{r.invoice_ref ? ` · ${r.invoice_ref}` : ""}{r.amount_ttc != null ? ` · ${fmt(r.amount_ttc)} €` : ""} <span style={{ color: "var(--text-muted)" }}>({r.file_name || "PDF"})</span></span>
                      <span style={{ color: "var(--text-muted)", textAlign: "right" }}>
                        {r.rejected_at ? fmtDate(r.rejected_at) : "date inconnue"} · {r.rejected_reason || "sans motif"} · {r.pdf_deleted_at ? "PDF supprimé" : "PDF conservé"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {ocrLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
          ) : ocrInvoices.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucune facture en attente</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Importez un PDF de facture fournisseur pour lancer l'extraction automatique.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ocrInvoices.map(inv => {
                const editing = ocrEditId === inv.id;
                const conf = confidenceBadge(inv.confidence_score);
                const busy = ocrActionId === inv.id;
                return (
                  <div key={inv.id} className="card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                      <input type="checkbox" style={{ marginTop: 4 }} aria-label="Sélectionner cette facture"
                        checked={ocrSelected.includes(inv.id)}
                        onChange={e => setOcrSelected(s => e.target.checked ? [...s, inv.id] : s.filter(x => x !== inv.id))} />
                      <div style={{ flex: 1, minWidth: 260 }}>
                        {inv.anomalies?.length > 0 && (
                          <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 6 }}>⚠ À vérifier : {inv.anomalies.join(" · ")}</div>
                        )}
                        {editing ? (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div style={{ gridColumn: "1/-1" }}>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Fournisseur *</label>
                              <input className="input" value={ocrEditForm.supplier_name} onChange={e => setOcrEditForm(f => ({ ...f, supplier_name: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Réf. facture</label>
                              <input className="input" value={ocrEditForm.invoice_ref} onChange={e => setOcrEditForm(f => ({ ...f, invoice_ref: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Date</label>
                              <input className="input" type="date" value={ocrEditForm.invoice_date} onChange={e => setOcrEditForm(f => ({ ...f, invoice_date: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>HT (€) *</label>
                              <input className="input" type="number" step="0.01" value={ocrEditForm.amount_ht} onChange={e => setOcrEditForm(f => ({ ...f, amount_ht: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>TVA (€) *</label>
                              <input className="input" type="number" step="0.01" value={ocrEditForm.amount_tva} onChange={e => setOcrEditForm(f => ({ ...f, amount_tva: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>TTC (€) *</label>
                              <input className="input" type="number" step="0.01" value={ocrEditForm.amount_ttc} onChange={e => setOcrEditForm(f => ({ ...f, amount_ttc: e.target.value }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Compte comptable</label>
                              {/* Recherche par numéro ou intitulé, groupes Charges (6) / Immobilisations (2) (A1b-2) */}
                              <AccountPicker value={ocrEditForm.suggested_account} width={120} ariaLabel="Compte de charge" placeholder="607000" {...purchaseAccountGroups}
                                choices={planAccounts.map(a => ({ ...a, kind: "general" }))} onCommit={v => setOcrEditForm(f => ({ ...f, suggested_account: v }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Nature de l'achat *</label>
                              <select className="input" value={ocrEditForm.operation_nature} onChange={e => setOcrEditForm(f => ({ ...f, operation_nature: e.target.value }))}>
                                <option value="">— À choisir —</option>
                                <option value="biens">Biens (marchandises, matériel, fournitures, énergie)</option>
                                <option value="services">Services (prestations, déplacements, honoraires, abonnements)</option>
                              </select>
                              <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 2 }}>TVA déductible : à la facture pour les biens, au paiement pour les services.</div>
                            </div>
                            {ocrEditForm.operation_nature === 'services' && (
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                                <input type="checkbox" id={`deb-${inv.id}`} checked={!!ocrEditForm.supplier_vat_on_debits} onChange={e => setOcrEditForm(f => ({ ...f, supplier_vat_on_debits: e.target.checked }))} style={{ marginTop: 3 }} />
                                <label htmlFor={`deb-${inv.id}`} style={{ fontSize: 11 }}>Fournisseur ayant opté pour les débits (mention sur la facture) : TVA déductible dès la facture</label>
                              </div>
                            )}
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Droit à déduction de la TVA</label>
                              <select className="input" value={ocrEditForm.deductibility_rule} onChange={e => setOcrEditForm(f => ({ ...f, deductibility_rule: e.target.value }))}>
                                <option value="standard">TVA déductible (droit commun)</option>
                                <option value="transport_personnes">Transport de personnes (train, avion, taxi) — non déductible</option>
                                <option value="hebergement">Hébergement des dirigeants et salariés — non déductible</option>
                                <option value="vehicule">Véhicule (achat, location, entretien)</option>
                                <option value="carburant">Carburant</option>
                                <option value="non_deductible">Non déductible (cadeau au-delà de 73 €, autre exclusion)</option>
                              </select>
                              {['vehicule', 'carburant'].includes(ocrEditForm.deductibility_rule) && (
                                <select className="input" style={{ marginTop: 4 }} value={ocrEditForm.vehicle_type} onChange={e => setOcrEditForm(f => ({ ...f, vehicle_type: e.target.value }))}>
                                  <option value="tourisme">Véhicule de tourisme (véhicule : 0 % ; carburant : 80 %)</option>
                                  <option value="utilitaire">Véhicule utilitaire (100 %)</option>
                                </select>
                              )}
                              <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 2 }}>La TVA non déductible est intégrée à la charge.</div>
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Régime de TVA</label>
                              <select className="input" value={ocrEditForm.vat_regime} onChange={e => setOcrEditForm(f => ({ ...f, vat_regime: e.target.value }))}>
                                <option value="">TVA facturée (standard)</option>
                                <option value="franchise">Franchise en base (art. 293 B) — TVA 0</option>
                                <option value="autoliquidation">Autoliquidation (prestataire UE / étranger)</option>
                                <option value="exoneration">Exonération de TVA</option>
                              </select>
                            </div>
                            {ocrEditForm.vat_regime === 'autoliquidation' && (
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Taux autoliquidé (%)</label>
                                <input className="input" type="number" step="0.1" value={ocrEditForm.autoliq_rate} onChange={e => setOcrEditForm(f => ({ ...f, autoliq_rate: e.target.value }))} />
                                <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 2 }}>TVA facturée : 0. La TVA due et la TVA déductible (HT × taux) sont passées en écriture.</div>
                              </div>
                            )}
                            {ocrEditForm.vat_regime === 'exoneration' && (
                              <div style={{ gridColumn: "1/-1" }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Article d'exonération *</label>
                                <input className="input" value={ocrEditForm.vat_exemption_article} placeholder="tel qu'il figure sur la facture, ex. art. 261 du CGI"
                                  onChange={e => setOcrEditForm(f => ({ ...f, vat_exemption_article: e.target.value }))} />
                                <div style={{ fontSize: 10.5, color: "#ba7517", marginTop: 2 }}>Sans mention d'exonération sur la facture, la TVA n'a probablement pas été lue : reportez-la plutôt (« dont TVA »).</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                              {inv.supplier_name || <span style={{ color: "#e24b4a" }}>Fournisseur non identifié</span>}
                              {inv.invoice_ref && <span style={{ fontWeight: 500, color: "var(--text-muted)", marginLeft: 8 }}>Réf. {inv.invoice_ref}</span>}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
                              {inv.invoice_date ? fmtDate(inv.invoice_date) : "Date inconnue"}
                              {" · "}
                              HT {inv.amount_ht != null ? fmt(inv.amount_ht) + " €" : "—"}
                              {" · "}TVA {inv.amount_tva != null ? fmt(inv.amount_tva) + " €" : "—"}
                              {" · "}<strong>TTC {inv.amount_ttc != null ? fmt(inv.amount_ttc) + " €" : "—"}</strong>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#185fa522", color: "#185fa5", fontWeight: 600 }}>
                                {OCR_LEVEL_LABEL[inv.ocr_level] || "Niveau inconnu"}
                              </span>
                              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: conf.bg, color: conf.color, fontWeight: 600 }}>
                                Confiance {conf.label}
                              </span>
                              {VAT_REGIME_LABEL[inv.vat_regime] && (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#8b5cf622", color: "#8b5cf6", fontWeight: 600 }}>
                                  {VAT_REGIME_LABEL[inv.vat_regime]}{inv.vat_regime === 'exoneration' && inv.vat_exemption_article ? ` — ${inv.vat_exemption_article}` : ''}
                                </span>
                              )}
                              {!!inv.prior_exercise && (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#64748b22", color: "#475569", fontWeight: 700 }}>
                                  Exercice antérieur — repris par FEC
                                </span>
                              )}
                              {inv.suggested_account && (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#64748b22", color: "#64748b", fontFamily: "monospace", fontWeight: 600 }}>
                                  Compte {inv.suggested_account}
                                  {inv.account_source === "keyword" ? ` · suggéré${inv.account_hint?.label ? ` (${inv.account_hint.label})` : ""}, à confirmer` : inv.account_source === "rule" ? " · règle apprise" : inv.account_source === "user" ? " · choisi" : ""}
                                </span>
                              )}
                              {!inv.suggested_account && (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#f59e0b22", color: "#b45309", fontWeight: 600 }}>
                                  Compte de charge à choisir (« Corriger »)
                                </span>
                              )}
                              {inv.deductibility_preview && inv.deductibility_rule !== 'standard' && (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#f59e0b22", color: "#b45309", fontWeight: 600 }}>
                                  TVA déductible {inv.deductibility_preview.deductible_vat.toFixed(2)} € · en charge {inv.deductibility_preview.nondeductible_vat.toFixed(2)} €
                                  {inv.deductibility_source === "suggested" ? " · suggéré, à confirmer" : inv.deductibility_source === "rule" ? " · appris" : " · choisi"}
                                </span>
                              )}
                              {inv.nature_required && (inv.operation_nature ? (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#0ea5e922", color: "#0369a1", fontWeight: 600 }}>
                                  {inv.operation_nature === "biens" ? "Biens" : "Services"}
                                  {inv.nature_source === "suggested" ? ` · suggéré${inv.nature_hint?.reason ? ` (${inv.nature_hint.reason})` : ""}, à confirmer` : inv.nature_source === "rule" ? " · appris" : " · choisi"}
                                  {inv.operation_nature === "services" && inv.supplier_vat_on_debits ? " · option débits" : ""}
                                </span>
                              ) : (
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, background: "#f59e0b22", color: "#b45309", fontWeight: 600 }}>
                                  Nature biens/services à choisir (« Corriger »)
                                </span>
                              ))}
                            </div>
                            {inv.new_supplier && (
                              <div style={{ marginTop: 6, padding: "5px 10px", background: "#fff7ed", borderRadius: 8, fontSize: 11.5 }}>
                                <strong>Nouveau fournisseur — code de l'expert-comptable</strong> (facultatif) :{" "}
                                <input className="input" style={{ display: "inline-block", width: 130, fontSize: 11.5, padding: "2px 6px" }} placeholder="ex. 401EDF" value={supplierCodes[inv.id] || ""}
                                  onChange={e => setSupplierCodes(c => ({ ...c, [inv.id]: e.target.value.toUpperCase().replace(/\s/g, "") }))} />
                                <span style={{ color: "#6b7280" }}> — {supplierCodes[inv.id] ? "le fournisseur sera créé sous ce code à la validation." : "vide : compte automatique, mention « code expert à attribuer »."}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {inv.file_url && (
                          <button className="btn" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => openBlobInNewTab(() => api.ocrInvoices.file(inv.id))}>📎 PDF</button>
                        )}
                        {editing ? (
                          <>
                            <button className="btn" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => setOcrEditId(null)}>Annuler</button>
                            <button className="btn primary" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => saveOcrEdit(inv.id)}>Enregistrer</button>
                          </>
                        ) : (
                          <>
                            <button className="btn" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => openOcrEdit(inv)}>✏️ Corriger</button>
                            <button className="btn" style={{ fontSize: 11, padding: "5px 10px", color: "#e24b4a" }} disabled={busy} onClick={() => openReject({ kind: 'one', inv })}>✕ Rejeter</button>
                            <button className="btn primary" style={{ fontSize: 11, padding: "5px 10px" }} disabled={busy || !!inv.prior_exercise}
                              title={inv.prior_exercise ? "Exercice antérieur, repris par FEC : non comptabilisée dans ExecPro360" : undefined}
                              onClick={() => validateOcrInvoice(inv)}>
                              {busy ? "…" : "✓ Valider"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {ocrRowError?.id === inv.id && !editing && (
                      <div role="alert" style={{ marginTop: 8, background: "#e24b4a14", color: "#b91c1c", padding: "6px 10px", borderRadius: 6, fontSize: 12.5 }}>
                        ⚠ Validation refusée : {ocrRowError.message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "ocr" && priorInvoices.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Exercice antérieur — repris par FEC ({priorInvoices.length})</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 8px" }}>
            Factures datées d'avant le début de l'exercice : elles sont comptabilisées par votre expert-comptable dans le FEC de clôture. Elles ne sont pas à valider ici ; leur PDF reste consultable.
          </div>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th style={{ textAlign: "left" }}>Fournisseur</th><th style={{ textAlign: "left" }}>Référence</th><th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "right" }}>TTC</th><th /></tr></thead>
            <tbody>{priorInvoices.map(p => (
              <tr key={p.id}>
                <td>{p.supplier_name || "—"}</td><td>{p.invoice_ref || "—"}</td>
                <td>{p.invoice_date ? String(p.invoice_date).slice(0, 10).split("-").reverse().join("/") : "—"}</td>
                <td style={{ textAlign: "right" }}>{p.amount_ttc != null ? `${Number(p.amount_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €` : "—"}</td>
                <td>{p.has_file ? <button className="btn" style={{ fontSize: 11, padding: "4px 9px" }} onClick={() => openBlobInNewTab(() => api.ocrInvoices.file(p.id))}>📎 PDF</button> : null}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Onglet À valider (J1) */}
      {tab === "tovalidate" && <PurchasesToValidate onOpenOcr={() => setTab("ocr")} onNavigate={onNavigate} />}

      {/* Onglet Factures fournisseurs */}
      {tab === "purchases" && (
        <div>
          <PurchaseNatureMissing onChanged={loadPurchases} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Factures fournisseurs</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Factures d'achat validées et comptabilisées — issues de la saisie manuelle ou du pipeline OCR</div>
            </div>
          </div>
          {/* À payer, puis un onglet par mois non soldé ; mois soldés dans le menu (lot 2) */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            {[{ key: "to_pay", label: `À payer (${purchaseMonths.to_pay.count} · ${fmt(purchaseMonths.to_pay.remaining)} €)` },
              { key: "settled_marked", label: `Réglées, à rapprocher (${purchaseMonths.settled_marked?.count || 0} · ${fmt(purchaseMonths.settled_marked?.remaining || 0)} €)` },
              { key: "all", label: "Toutes" },
              ...purchaseMonths.months.filter(m => !m.settled).map(m => ({ key: m.month, label: `${monthLabel(m.month)} (${m.to_pay} à payer)` }))
            ].map(t => (
              <button key={t.key} onClick={() => setPurchasesView(t.key)} style={{
                padding: "5px 12px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer",
                border: "1px solid #e5e7eb", background: purchasesView === t.key ? "var(--g)" : "#fff",
                color: purchasesView === t.key ? "#fff" : "var(--text-muted)",
              }}>{t.label}</button>
            ))}
            {purchaseMonths.months.some(m => m.settled) && (
              <select className="input" style={{ fontSize: 11, padding: "4px 8px", width: "auto" }} value={purchaseMonths.months.some(m => m.settled && m.month === purchasesView) ? purchasesView : ""}
                onChange={e => e.target.value && setPurchasesView(e.target.value)}>
                <option value="">Mois soldés…</option>
                {purchaseMonths.months.filter(m => m.settled).map(m => <option key={m.month} value={m.month}>{monthLabel(m.month)} ({m.count})</option>)}
              </select>
            )}
          </div>
          {purchasesView === "to_pay" && purchases.length > 0 && (
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <div>Total à payer : <strong>{fmt(purchasesRemaining)} €</strong> sur {purchases.length} facture(s), reste dû calculé d'après les paiements rapprochés ou ventilés du relevé.</div>
              {(purchaseMonths.settled_marked?.count || 0) > 0 && (
                <div style={{ color: "#6d28d9", marginTop: 3 }}>Réglées, à rapprocher : <strong>{fmt(purchaseMonths.settled_marked.remaining)} €</strong> ({purchaseMonths.settled_marked.count} facture(s) — encore dues en comptabilité tant que le paiement n'est pas relié au relevé).</div>
              )}
              {purchaseTotals.to_pay_by_supplier.length > 0 && (
                <details style={{ marginTop: 4 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12.5 }}>Total à payer par fournisseur ({purchaseTotals.to_pay_by_supplier.length})</summary>
                  <table style={{ fontSize: 12, marginTop: 4, borderCollapse: "collapse" }}><tbody>
                    {purchaseTotals.to_pay_by_supplier.map(s => (
                      <tr key={s.supplier_name}>
                        <td style={{ padding: "2px 12px 2px 0" }}><a href="#f" onClick={e => { e.preventDefault(); setSupplierFilter(s.supplier_name); }}>{s.supplier_name}</a></td>
                        <td style={{ padding: "2px 12px 2px 0", color: "var(--text-muted)" }}>{s.count} facture(s)</td>
                        <td style={{ padding: "2px 0", textAlign: "right", fontWeight: 600 }}>{fmt(s.remaining)} €</td>
                      </tr>
                    ))}
                  </tbody></table>
                </details>
              )}
            </div>
          )}
          {purchasesView === "settled_marked" && purchases.length > 0 && (
            <div style={{ fontSize: 13, marginBottom: 8, color: "#6d28d9" }}>Réglées, à rapprocher : <strong>{fmt(purchasesRemaining)} €</strong> sur {purchases.length} facture(s) — encore dues en comptabilité tant que le paiement n'est pas relié au relevé. Reliez-les dans Comptes bancaires > « 🔎 Proposer les factures », onglet « Paiements fournisseurs » : le marqueur se lève alors de lui-même.</div>
          )}
          {purchases.length > 0 && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, marginBottom: 8 }}>
              <label>Fournisseur :{" "}
                <select className="input" style={{ fontSize: 12, padding: "3px 8px", width: "auto" }} value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
                  <option value="">Tous</option>
                  {[...new Set(purchases.map(p => p.supplier_name || "—"))].sort((a, b) => a.localeCompare(b, "fr")).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label>Tri :{" "}
                <select className="input" style={{ fontSize: 12, padding: "3px 8px", width: "auto" }} value={dateSort} onChange={e => setDateSort(e.target.value)}>
                  <option value="desc">Date, plus récentes d'abord</option>
                  <option value="asc">Date, plus anciennes d'abord</option>
                </select>
              </label>
              {canChangeNature && markSelected.length > 0 && (
                <>
                  <button className="btn primary" style={{ fontSize: 12, padding: "4px 10px" }} disabled={markBusy}
                    onClick={() => markSettled(markSelected.filter(id => isToPay(purchases.find(p => p.id === id) || {})), true)}>Marquer « Déjà réglée — à rapprocher » ({markSelected.filter(id => isToPay(purchases.find(p => p.id === id) || {})).length})</button>
                  <button className="btn" style={{ fontSize: 12, padding: "4px 10px" }} disabled={markBusy}
                    onClick={() => markSettled(markSelected.filter(id => (purchases.find(p => p.id === id) || {}).payment_state === SETTLED_MARKED), false)}>Retirer le marqueur ({markSelected.filter(id => (purchases.find(p => p.id === id) || {}).payment_state === SETTLED_MARKED).length})</button>
                </>
              )}
            </div>
          )}
          {markResult && (
            <div style={{ fontSize: 12.5, marginBottom: 8, color: markResult.refused?.length ? "#b45309" : "#166534" }}>
              {markResult.marked ? `${markResult.done} facture(s) marquée(s) « Déjà réglée — à rapprocher ».` : `Marqueur retiré sur ${markResult.done} facture(s).`}
              {(markResult.refused || []).map(r => <div key={r.id}>{r.number || `#${r.id}`} : {r.reason}</div>)}
            </div>
          )}
          {!["to_pay", "settled_marked"].includes(purchasesView) && purchases.length > 0 && (() => {
            const due = purchases.filter(isToPay);
            return (
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 13, marginBottom: 8 }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="checkbox" checked={onlyToPay} onChange={e => setOnlyToPay(e.target.checked)} /> À payer seulement
                </label>
                <span>{purchasesView === "all" ? "Restant dû" : "Restant dû ce mois-ci"} : <strong>{fmt(due.reduce((s, p) => s + (Number(p.remaining) || 0), 0))} €</strong> sur {due.length} facture(s) à payer</span>
              </div>
            );
          })()}

          {purchasesError && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{purchasesError}</div>}

          {purchasesLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
          ) : purchases.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{purchasesView === "to_pay" ? "Aucune facture à payer" : purchasesView === "settled_marked" ? "Aucune facture réglée à rapprocher" : purchasesView === "all" ? "Aucune facture fournisseur" : "Aucune facture fournisseur ce mois-ci"}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>{purchasesView === "to_pay" ? "Toutes les factures validées sont réglées (paiements rapprochés ou ventilés du relevé)." : "Importez une facture via l'onglet Factures OCR ou l'écran Abonnements."}</div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                    {canChangeNature && (
                      <th style={{ padding: "9px 6px 9px 14px" }}>
                        <input type="checkbox" title="Tout sélectionner" checked={visiblePurchases.length > 0 && visiblePurchases.every(p => markSelected.includes(p.id))}
                          onChange={e => setMarkSelected(e.target.checked ? visiblePurchases.filter(p => isToPay(p) || p.payment_state === SETTLED_MARKED).map(p => p.id) : [])} />
                      </th>
                    )}
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Fournisseur</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Réf. fournisseur</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>N° interne</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Date</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Échéance</th>
                    <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>HT</th>
                    <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>TTC</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Compte</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Statut</th>
                    <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Paiement</th>
                    <th style={{ padding: "9px 14px", fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePurchases.map(p => {
                    // « Exercice antérieur, repris par FEC » : conservée, sans écriture
                    const sc = p.prior_exercise
                      ? { label: "Exercice antérieur — repris par FEC", color: "#475569", bg: "#64748b22" }
                      : (PURCHASE_STATUS[p.status] || PURCHASE_STATUS.draft);
                    return (
                      <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                        {canChangeNature && (
                          <td style={{ padding: "10px 6px 10px 14px" }}>
                            {(isToPay(p) || p.payment_state === SETTLED_MARKED) && (
                              <input type="checkbox" checked={markSelected.includes(p.id)} onChange={e => setMarkSelected(s => e.target.checked ? [...s, p.id] : s.filter(x => x !== p.id))} />
                            )}
                          </td>
                        )}
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>{p.supplier_name}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace" }}>{p.supplier_ref || "—"}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)" }}><DocBadge kind="achat" />{p.number}</td>
                        <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{fmtDate(p.date)}</td>
                        <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{p.due_date ? fmtDate(p.due_date) : "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right" }}>{fmt(p.ht_amount)} €</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{fmt(p.ttc_amount)} €</td>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)" }}>{p.purchase_account}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                          {p.operation_nature && !p.prior_exercise && p.status !== 'cancelled' && (
                            <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 3 }}>
                              {p.operation_nature === "biens" ? "Biens" : "Services"}{p.operation_nature === "services" ? (p.vat_exigibility === "paiement" ? " · TVA au paiement" : " · option débits") : ""}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 11.5 }}>
                          {p.payment_state && (
                            <>
                              {p.payment_state === SETTLED_MARKED ? (
                                <>
                                  <span style={{ fontWeight: 700, color: "#6d28d9", background: "#6d28d914", padding: "2px 7px", borderRadius: 8 }}>Réglée — à rapprocher</span>
                                  <div style={{ color: "var(--text-muted)" }}>marquée{p.settled_marked_by_name ? ` par ${p.settled_marked_by_name}` : ""} le {fmtDate(p.settled_marked_at)}</div>
                                  <div>Encore due en comptabilité : <strong>{fmt(p.remaining)} €</strong></div>
                                </>
                              ) : (
                                <span style={{ fontWeight: 700, color: p.payment_state === "payée" ? "#16a34a" : p.payment_state === "à payer" ? "#b45309" : "#1d4ed8" }}>
                                  {p.payment_state === "payée" ? "Payée" : p.payment_state === "à payer" ? "À payer" : "Partiellement payée"}
                                </span>
                              )}
                              {isToPay(p) && <div>Reste dû : <strong>{fmt(p.remaining)} €</strong></div>}
                              {(p.payments || []).map((x, k) => (
                                <div key={k} style={{ color: "var(--text-muted)" }}>
                                  {fmtDate(x.date)} · {fmt(x.amount)} € {x.origin === "ventilation" ? "(ventilé)" : x.origin === "saisie" ? "(saisi)" : ""}
                                  {x.import_id && <> · <a href="#releve" onClick={e => { e.preventDefault(); try { sessionStorage.setItem("execpro360.openImport", String(x.import_id)); } catch {} onNavigate?.("banque"); }}>ligne du relevé</a></>}
                                </div>
                              ))}
                            </>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          {canChangeNature && isToPay(p) && (
                            <button className="btn" style={{ fontSize: 11, padding: "3px 8px", marginRight: 6 }} disabled={markBusy} onClick={() => markSettled([p.id], true)}
                              title="Paiement déjà fait, pas encore relié au relevé : sort du « À payer », aucune écriture">Déjà réglée</button>
                          )}
                          {canChangeNature && p.payment_state === SETTLED_MARKED && (
                            <button className="btn" style={{ fontSize: 11, padding: "3px 8px", marginRight: 6 }} disabled={markBusy} onClick={() => markSettled([p.id], false)}
                              title="Retirer le marqueur : la facture revient dans « À payer »">Retirer le marqueur</button>
                          )}
                          {canChangeNature && ['validated', 'paid'].includes(p.status) && !p.prior_exercise && (
                            <button className="btn" style={{ fontSize: 11, padding: "3px 8px", marginRight: 6 }} onClick={() => openNature(p)}
                              title="Corriger la nature biens / services d'un achat validé (écriture de correction à la date de la facture)">Nature</button>
                          )}
                          {/* 📎 PDF ; sans PDF : « PDF absent » et les informations disponibles (A1b) */}
                          {p.status !== "draft" && <InvoicePdfButton kind="achat" id={p.id} style={{ marginRight: 6 }} />}
                          {p.status !== 'cancelled' && !p.prior_exercise && (
                            <button className="btn" style={{ fontSize: 11, padding: "3px 8px", color: "#e24b4a" }} onClick={() => openCancel(p)}>🗑 Annuler</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal : modifier la nature d'un achat validé */}
      {natureTarget && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 540, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Nature de l'achat {natureTarget.p.number}</div>
            <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
              {natureTarget.p.supplier_name} — {fmt(natureTarget.p.ttc_amount)} € TTC, facture du {fmtDate(natureTarget.p.date)}.
              Nature actuelle : <strong>{natureTarget.p.operation_nature === "biens" ? "biens" : natureTarget.p.operation_nature === "services" ? `services${natureTarget.p.vat_exigibility === "paiement" ? " (TVA au paiement)" : " (option débits)"}` : "non renseignée"}</strong>.
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 13, marginBottom: 8 }}>
              {[["biens", "Biens (TVA à la facture)"], ["services", "Services (TVA au paiement)"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" name="nature" checked={natureTarget.nature === v} onChange={() => previewNature(natureTarget.p, v, false)} />{l}
                </label>
              ))}
            </div>
            {natureTarget.nature === "services" && (
              <label style={{ display: "flex", gap: 6, fontSize: 12.5, marginBottom: 10 }}>
                <input type="checkbox" checked={natureTarget.onDebits} onChange={e => previewNature(natureTarget.p, "services", e.target.checked)} />
                Fournisseur ayant opté pour les débits (mention sur la facture) : TVA déductible à la facture
              </label>
            )}
            {naturePreview?.loading && <div style={{ fontSize: 13, marginBottom: 12 }}>Préparation de l'écriture…</div>}
            {naturePreview?.error && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: "8px 10px", background: "#fef2f2", color: "#991b1b", borderRadius: 8 }}>Modification impossible : {naturePreview.error}</div>
            )}
            {naturePreview && !naturePreview.loading && !naturePreview.error && (
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                {naturePreview.od.length ? (<>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Écriture de correction (OD), à la date de la facture{naturePreview.od.some(l => l.date !== naturePreview.date) ? " et des paiements déjà connus" : ""} :</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ color: "var(--text-muted)", textAlign: "left" }}><th style={{ padding: "3px 4px" }}>Date</th><th style={{ padding: "3px 4px" }}>Compte</th><th style={{ padding: "3px 4px", textAlign: "right" }}>Débit</th><th style={{ padding: "3px 4px", textAlign: "right" }}>Crédit</th></tr></thead>
                    <tbody>{naturePreview.od.map((l, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                        <td style={{ padding: "3px 4px" }}>{fmtDate(l.date)}</td>
                        <td style={{ padding: "3px 4px" }}>{l.account_code} {l.account_name}</td>
                        <td style={{ padding: "3px 4px", textAlign: "right" }}>{l.debit ? fmt(l.debit) : ""}</td>
                        <td style={{ padding: "3px 4px", textAlign: "right" }}>{l.credit ? fmt(l.credit) : ""}</td>
                      </tr>))}</tbody>
                  </table>
                </>) : <div>Aucune écriture de TVA à passer (TVA nulle ou déjà à la bonne place) : seule la nature est enregistrée.</div>}
                {naturePreview.payments.length > 0 && (
                  <div style={{ marginTop: 8, color: "var(--text-muted)" }}>
                    {naturePreview.payments.length} paiement(s) déjà connu(s) : la TVA du service deviendra déductible à leur date ({naturePreview.payments.map(x => `${fmtDate(x.date)} ${fmt(x.amount)} €`).join(", ")}).
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={closeNature} disabled={cancelling}>Fermer</button>
              <button className="btn primary" onClick={confirmNature} disabled={cancelling || !naturePreview || naturePreview.loading || !!naturePreview.error}>
                {cancelling ? "…" : "Confirmer la nature"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation annulation facture fournisseur */}
      {cancelTarget && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 520, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Annuler la facture {cancelTarget.number}</div>
            <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
              Cette action contre-passe l'écriture comptable de la facture ({fmt(cancelTarget.ttc_amount)} € — {cancelTarget.supplier_name}). L'opération est tracée et réversible uniquement par une nouvelle écriture manuelle.
            </div>
            {cancelPreview?.loading && <div style={{ fontSize: 13, marginBottom: 12 }}>Préparation de la contre-passation…</div>}
            {cancelPreview?.error && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: "8px 10px", background: "#fef2f2", color: "#991b1b", borderRadius: 8 }}>
                Annulation impossible : {cancelPreview.error}
              </div>
            )}
            {cancelPreview && !cancelPreview.loading && !cancelPreview.error && (
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                {cancelPreview.already_reversed || !cancelPreview.reversal.length ? (
                  <div>Aucune écriture à contre-passer : la facture sera seulement marquée annulée.</div>
                ) : (<>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Contre-passation au {fmtDate(cancelPreview.date)} :</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
                      <th style={{ padding: "3px 4px" }}>Journal</th><th style={{ padding: "3px 4px" }}>Compte</th>
                      <th style={{ padding: "3px 4px", textAlign: "right" }}>Débit</th><th style={{ padding: "3px 4px", textAlign: "right" }}>Crédit</th>
                    </tr></thead>
                    <tbody>{cancelPreview.reversal.map((l, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                        <td style={{ padding: "3px 4px" }}>{l.journal}</td>
                        <td style={{ padding: "3px 4px" }}>{l.account_code} {l.account_name || ""}</td>
                        <td style={{ padding: "3px 4px", textAlign: "right" }}>{l.debit ? fmt(l.debit) : ""}</td>
                        <td style={{ padding: "3px 4px", textAlign: "right" }}>{l.credit ? fmt(l.credit) : ""}</td>
                      </tr>))}</tbody>
                  </table>
                </>)}
                {cancelPreview.pending_bank_lines > 0 && (
                  <div style={{ marginTop: 8, color: "var(--text-muted)" }}>{cancelPreview.pending_bank_lines} ligne(s) de relevé en attente rapprochée(s) de cette facture : le rapprochement sera retiré.</div>
                )}
              </div>
            )}
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Motif (recommandé)</label>
            <input className="input" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="ex: doublon, erreur de saisie…" style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={closeCancel} disabled={cancelling}>Annuler l'action</button>
              <button className="btn primary" style={{ background: "#e24b4a" }} onClick={confirmCancel} disabled={cancelling || !cancelPreview || cancelPreview.loading || !!cancelPreview.error}>
                {cancelling ? "…" : "Confirmer l'annulation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal doublon probable : « C'est une facture distincte » */}
      {dupTarget && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 480, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Doublon possible</div>
            <div style={{ fontSize: 13, marginBottom: 10 }}>
              Facture à valider : <strong>{dupTarget.inv.supplier_name}</strong>
              {dupTarget.inv.invoice_ref ? ` — réf. ${dupTarget.inv.invoice_ref}` : ""} — {fmtDate(dupTarget.inv.invoice_date)} — {fmt(dupTarget.inv.amount_ttc)} € TTC
            </div>
            <div style={{ fontSize: 13, marginBottom: 12, padding: "8px 10px", background: "#fff7ed", borderRadius: 8 }}>
              Déjà comptabilisée :{" "}
              {dupTarget.existing
                ? <><strong>{dupTarget.existing.number}</strong> du {fmtDate(dupTarget.existing.date)} — {fmt(dupTarget.existing.ttc_amount)} €{dupTarget.existing.invoice_ref ? ` — réf. ${dupTarget.existing.invoice_ref}` : ""}</>
                : dupTarget.entry ? <>écriture <strong>{dupTarget.entry.piece}</strong> du {fmtDate(dupTarget.entry.date)}</> : "une facture du même fournisseur, même montant, date proche"}
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginBottom: 10 }}>
              Ouvrez les deux PDF pour comparer. Si ce sont bien deux factures différentes (autre billet, autre période…), confirmez : la validation sera tracée au journal d'audit.
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Pourquoi est-ce une facture distincte ? (recommandé)</label>
            <input className="input" value={dupReason} onChange={e => setDupReason(e.target.value)} placeholder="ex : autre voyageur, autre billet" style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {dupTarget.inv.file_url && <button className="btn" onClick={() => openBlobInNewTab(() => api.ocrInvoices.file(dupTarget.inv.id))}>📎 Voir ce PDF</button>}
              <button className="btn" onClick={() => setDupTarget(null)} disabled={ocrActionId === dupTarget.inv.id}>Annuler</button>
              <button className="btn primary" disabled={ocrActionId === dupTarget.inv.id}
                onClick={() => validateOcrInvoice(dupTarget.inv, { force_despite_duplicate_warning: true, duplicate_reason: dupReason || undefined })}>
                {ocrActionId === dupTarget.inv.id ? "…" : "C'est une facture distincte"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal rejet de factures importées (une, sélection ou lot) */}
      {rejectTarget && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 460, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              {rejectTarget.kind === 'one' && `Rejeter la facture de « ${rejectTarget.inv.supplier_name || 'fournisseur inconnu'} »`}
              {rejectTarget.kind === 'many' && `Rejeter ${rejectTarget.ids.length} facture(s) sélectionnée(s)`}
              {rejectTarget.kind === 'batch' && "Annuler tout ce lot d'import"}
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
              Les factures rejetées quittent la liste d'attente, sans aucune écriture comptable. Une trace est conservée
              (motif, date, auteur) et la facture pourra être réimportée.
              {rejectTarget.kind === 'batch' && " Seules les factures du lot encore en attente sont rejetées ; celles déjà validées ne sont pas modifiées."}
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Motif (recommandé)</label>
            <input className="input" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="ex : doublon, mauvais fichier, facture de vente…" style={{ marginBottom: 10 }} />
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={rejectDeletePdf} onChange={e => setRejectDeletePdf(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Supprimer aussi le(s) PDF du serveur <span style={{ color: "var(--text-muted)" }}>(définitif ; la trace du rejet est conservée)</span></span>
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setRejectTarget(null)} disabled={rejecting}>Annuler l'action</button>
              <button className="btn primary" style={{ background: "#e24b4a" }} onClick={confirmReject} disabled={rejecting}>
                {rejecting ? "…" : "Confirmer le rejet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal escompte fournisseur */}
      {showDiscModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{editDisc ? "Modifier l'escompte" : "Nouvel escompte fournisseur"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowDisc(false)}>✕</button>
            </div>
            {discError && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{discError}</div>}
            <form onSubmit={handleSaveDisc}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Réf. facture</label>
                  <input className="input" value={discForm.invoice_ref} onChange={e => setDiscForm(f => ({ ...f, invoice_ref: e.target.value }))} placeholder="FA-FOURNISSEUR-001" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux escompte (%) *</label>
                  <input className="input" type="number" step="0.01" required value={discForm.rate} onChange={e => setDiscForm(f => ({ ...f, rate: e.target.value }))} placeholder="2.00" />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Fournisseur</label>
                  <input className="input" value={discForm.client_name} onChange={e => setDiscForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Nom du fournisseur" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant (€)</label>
                  <input className="input" type="number" step="0.01" value={discForm.amount} onChange={e => setDiscForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Délai paiement (j)</label>
                  <input className="input" type="number" value={discForm.discount_days} onChange={e => setDiscForm(f => ({ ...f, discount_days: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date paiement effectif</label>
                  <input className="input" type="date" value={discForm.payment_date} onChange={e => setDiscForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
                  <input className="input" value={discForm.notes} onChange={e => setDiscForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observations…" />
                </div>
              </div>
              {discForm.rate && discForm.amount && parseFloat(discForm.amount) > 0 && (
                <div style={{ marginTop: 10, padding: "8px 12px", background: "#1a9e7208", border: "1px solid #1a9e7220", borderRadius: 8, fontSize: 12, color: "#1a9e72" }}>
                  Écriture : D&nbsp;401 ({parseFloat(discForm.amount || 0).toFixed(2)} €) / C&nbsp;765 — Produit financier net
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" className="btn" onClick={() => setShowDisc(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={savingDisc}>{savingDisc ? "…" : editDisc ? "Modifier" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal fournisseur */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 999, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
          <div className="card" style={{ width: "100%", maxWidth: 520 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{editTarget ? "Modifier le fournisseur" : "Nouveau fournisseur"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom du fournisseur *</label>
                  <input className="input" value={form.name} onChange={e => set("name", e.target.value)} required placeholder="Nom" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Code interne</label>
                  <input className="input" value={form.code} onChange={e => set("code", e.target.value)} placeholder="FOUR-001" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label>
                  <input className="input" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="contact@fournisseur.fr" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Téléphone</label>
                  <input className="input" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="01 23 45 67 89" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Ville</label>
                  <input className="input" value={form.city} onChange={e => set("city", e.target.value)} placeholder="Paris" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresse</label>
                  <input className="input" value={form.address} onChange={e => set("address", e.target.value)} placeholder="1 rue de la Paix" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>SIRET</label>
                  <input className="input" value={form.siret} onChange={e => set("siret", e.target.value)} placeholder="12345678900011" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>N° TVA</label>
                  <input className="input" value={form.tva_number} onChange={e => set("tva_number", e.target.value)} placeholder="FR12345678900" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Délai paiement (jours)</label>
                  <input className="input" type="number" value={form.payment_delay} onChange={e => set("payment_delay", e.target.value)} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
                  <textarea className="input" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} style={{ resize: "vertical" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)} disabled={saving}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>
                  {saving ? "Enregistrement…" : editTarget ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 380, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Supprimer le fournisseur</div>
            <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 20 }}>
              Supprimer "{confirmDelete.name}" ? Cette action est irréversible.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button className="btn primary" style={{ background: "#e24b4a" }} onClick={() => handleDelete(confirmDelete.id)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
